import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  SafetyCommunication,
  SafetyCommunicationAcknowledgement,
  AcknowledgementStatusResponse,
  PaginatedResponse,
} from '@/lib/types';

const API = '/api/v1/compliance/communications';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface CommunicationFilters {
  page?: number;
  page_size?: number;
  search?: string;
  category?: string;
  target_audience?: string;
  date_from?: string;
  date_to?: string;
  ack_status?: string;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── List / Detail ──────────────────────────────────────────────────────────

export function useSafetyCommunications(filters: CommunicationFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.safetyCommunications.list(clean),
    queryFn: async (): Promise<PaginatedResponse<SafetyCommunication>> => {
      const response = await apiClient.get<PaginatedResponse<SafetyCommunication>>(
        `${API}/`,
        { params: clean },
      );
      return response.data;
    },
  });
}

export function useSafetyCommunication(id: number) {
  return useQuery({
    queryKey: queryKeys.safetyCommunications.detail(id),
    queryFn: async (): Promise<SafetyCommunication> => {
      const response = await apiClient.get<SafetyCommunication>(`${API}/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

// ─── Acknowledgement Status ─────────────────────────────────────────────────

export function useAcknowledgementStatus(id: number) {
  return useQuery({
    queryKey: queryKeys.safetyCommunications.ackStatus(id),
    queryFn: async (): Promise<AcknowledgementStatusResponse> => {
      const response = await apiClient.get<AcknowledgementStatusResponse>(
        `${API}/${id}/acknowledgement-status/`,
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

// ─── Create ─────────────────────────────────────────────────────────────────

export function useCreateSafetyCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<SafetyCommunication> => {
      const response = await apiClient.post<SafetyCommunication>(`${API}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyCommunications.all });
    },
  });
}

// ─── Acknowledge ────────────────────────────────────────────────────────────

export function useAcknowledgeCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<SafetyCommunicationAcknowledgement> => {
      const response = await apiClient.post<SafetyCommunicationAcknowledgement>(
        `${API}/${id}/acknowledge/`,
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyCommunications.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.safetyCommunications.ackStatus(id) });
      qc.invalidateQueries({ queryKey: queryKeys.safetyCommunications.lists() });
    },
  });
}
